import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useWeddingCategories } from '../../../hooks/useWeddingCategories';
import { ArrowLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageUpload from '../../../components/ui/ImageUpload';
import LocationPicker from '../../../components/weddings/LocationPicker';


export default function ManageVendorProfile() {
    const navigate = useNavigate();
    const { data: categories = [] } = useWeddingCategories();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [userId, setUserId] = useState<string | null>(null);

    const [form, setForm] = useState({
        name: '',
        category_id: '',
        description: '',
        city: '',
        address: '',
        latitude: 0,
        longitude: 0,
        service_radius_km: 50, // Default
        capacity: 1, // Default
        contact_email: '',
        contact_phone: '',
        website: '',
        images: [] as string[],
        details: {} as Record<string, any>,
        tasting_available: false,
    });

    useEffect(() => {
        const fetchVendor = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                navigate('/');
                return;
            }

            setUserId(session.user.id);

            const { data: vendor } = await supabase
                .from('wedding_vendors')
                .select('*')
                .eq('owner_id', session.user.id)
                .single();

            if (vendor) {
                setForm({
                    name: vendor.name || '',
                    category_id: vendor.category_id || '',
                    description: vendor.description || '',
                    city: vendor.city || '',
                    address: vendor.address || '',
                    latitude: vendor.latitude || 0,
                    longitude: vendor.longitude || 0,
                    service_radius_km: vendor.service_radius_km || 50,
                    capacity: vendor.capacity || 1,
                    contact_email: vendor.contact_email || '',
                    contact_phone: vendor.contact_phone || '',
                    website: vendor.website || '',
                    images: vendor.images || [],
                    details: vendor.details || {},
                    tasting_available: vendor.details?.tasting_available || false,
                });
            } else {
                setIsNew(true);
            }

            setLoading(false);
        };

        fetchVendor();
    }, [navigate]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');

            if (isNew) {
                // Merge tasting_available into details
                const finalDetails = {
                    ...form.details,
                    tasting_available: form.tasting_available
                };

                const { error } = await supabase.from('wedding_vendors').insert({
                    owner_id: session.user.id,
                    ...form,
                    details: finalDetails // Save merged details
                });
                if (error) throw error;
                toast.success('Profile created!');
                setIsNew(false);
            } else {
                // Merge tasting_available into details for update too
                const finalDetails = {
                    ...form.details,
                    tasting_available: form.tasting_available
                };

                // Remove temp fields if any before sending to DB?
                // actually ...form sends everything in state matching column names.
                // tasting_available is NOT a column, so it might error or be ignored depending on strictness.
                // It's safer to destructure it out.

                const { tasting_available, ...submissionData } = form;

                const { error } = await supabase
                    .from('wedding_vendors')
                    .update({
                        ...submissionData,
                        details: finalDetails
                    })
                    .eq('owner_id', session.user.id);
                if (error) throw error;
                toast.success('Profile updated!');
            }
            navigate('/vendor/dashboard');
        } catch (err: any) {
            toast.error(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };



    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-500">Loading...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
            <div className="max-w-3xl mx-auto px-4 py-8">
                <button
                    onClick={() => navigate('/vendor/dashboard')}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
                >
                    <ArrowLeft size={20} />
                    Back to Dashboard
                </button>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        {isNew ? 'Create Your Profile' : 'Edit Profile'}
                    </h1>

                    <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Business Name *
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Category *
                                </label>
                                <select
                                    value={form.category_id}
                                    onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                >
                                    <option value="">Select category</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Description
                            </label>
                            <textarea
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                rows={4}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>

                        {/* Location */}
                        <div className="space-y-4 border-t border-gray-100 dark:border-gray-700 pt-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Location & Service Area</h3>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Base Location
                                </label>
                                <LocationPicker
                                    placeholder="Search your base city or address..."
                                    initialAddress={form.address || form.city}
                                    onLocationSelect={(lat, lon, address) => {
                                        // Simple heuristic: split address by comma to get "City"
                                        const city = address?.split(',')[0] || '';
                                        setForm({
                                            ...form,
                                            latitude: lat,
                                            longitude: lon,
                                            address: address || '',
                                            city: city
                                        });
                                    }}
                                />
                                <p className="text-xs text-gray-500">
                                    This location will be used to calculate your distance from clients.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        City (Display Name)
                                    </label>
                                    <input
                                        type="text"
                                        value={form.city}
                                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Service Radius: <span className="text-rose-600 font-bold">{form.service_radius_km} km</span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="500"
                                        step="5"
                                        value={form.service_radius_km}
                                        onChange={(e) => setForm({ ...form, service_radius_km: parseInt(e.target.value) })}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-600"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>0 km (Venue)</span>
                                        <span>500 km</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        How far are you willing to travel for a booking?
                                    </p>
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Booking Capacity (Units per Day)
                                </label>
                                <div className="flex items-center gap-4">
                                    <input
                                        type="number"
                                        min="1"
                                        value={form.capacity}
                                        onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })}
                                        className="w-24 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                    <p className="text-sm text-gray-500 flex-1">
                                        How many simultaneous bookings can you handle per day? (e.g., number of cars, rooms, or teams).
                                        Set to <b>1</b> for exclusive bookings.
                                    </p>
                                </div>
                            </div>

                            {/* Catering Specific: Tasting Session */}
                            {categories.find(c => c.id === form.category_id)?.slug === 'catering' && (
                                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="tasting_available"
                                            checked={form.tasting_available}
                                            onChange={(e) => setForm({ ...form, tasting_available: e.target.checked })}
                                            className="w-5 h-5 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                                        />
                                        <label htmlFor="tasting_available" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Tasting Session Available?
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1 ml-8">
                                        If checked, customers can request a tasting session directly from your profile.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Contact */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={form.contact_email}
                                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    value={form.contact_phone}
                                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    value={form.website}
                                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                                    placeholder="https://"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>

                        {/* Gallery */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Gallery Images
                            </label>
                            {/* Pass correct path for storing profile images */}
                            {userId && (
                                <ImageUpload
                                    bucket="vendor-images"
                                    path={`${userId}/profile`}
                                    currentImages={form.images}
                                    onUpload={(urls) => setForm({ ...form, images: urls })}
                                    maxFiles={10}
                                />
                            )}
                        </div>

                        {/* Save Button */}
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={handleSave}
                                disabled={saving || !form.name || !form.category_id}
                                className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                <Save size={20} />
                                {saving ? 'Saving...' : isNew ? 'Create Profile' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
